import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import ReusableDataTable from '../DataTable';
import { useSidebarActions } from '../../contexts/SidebarContext';
import FilterBar from '../FilterBar';
import { useNavigate } from 'react-router-dom';

const GenericPage = ({
    title,
    sidebarClassName = 'right-sidebar-lg',
    state,
    actions,
    FormComponent,
    ViewComponent,
    SubActionComponent,
    columns,
    selectableRows,
    onSelectedRowsChange,
    moduleName,
    moduleKey,
    customForm,
    customComponents = {},
    extraProps = {},
    availableActions = ['edit', 'delete'], // Default actions if not provided
    actionsColumnWidth = '150px', // Default width if not provided
    actionsColumnPosition = 'end', // New prop to control actions column position ('start', 'end', or index number)
    urlKey, // New prop for URL routing
    actionType = 'sidebar', // New prop to determine action type (sidebar/url)
    selectedIds = [],
    externalCondition = {},
    onClickedRow = () => { },
    clickedRowHighlight = false,
    noTable = false,
    customViewComponent,
    /** Rendered inside the card between the page title and the data table. */
    tableToolbar,
    /** When true, FilterBar does not render the legacy ClassicFilter row. */
    hideClassicFilter = false,
    /** When true, columns with resizable: true can be drag-resized in the table. */
    enableResize = false,
    /** When enableResize is true, allow resizing the auto-generated Actions column. */
    actionsColumnResizable = false,
    customActions = [],  // Array of action definitions: [{ key, label, icon, buttonClass, condition(row) }]
    handleCustomAction = null,  // Callback: (actionKey, row) => void
    rowActionConditions = {},  // e.g. { edit: (row) => !row.is_readonly, delete: (row) => !row.is_readonly }
}) => {
    const { toggleSidebar, closeSidebar } = useSidebarActions();
    const isInitialized = useRef(false);
    const navigate = useNavigate();

    const pageChangeInitialized     = useRef(false);
    const rowsPerPageInitialized    = useRef(false);
    const sortingInitialized        = useRef(false);

    // Download function with proper dependencies
    const handleDownload = useCallback(async (type) => {
        try {
            await actions.download(type);
        } catch (error) {
            console.error('Downloading error:', error);
        }
    }, [actions]);

    // Fetch function with proper dependencies
    const fetchData = useCallback(async (queryFilter = {}, activeFilter = {}) => {
        try {
            const shouldUseFilter = availableActions.some(item => item === 'filter');
            await actions.fetch(queryFilter, activeFilter, shouldUseFilter, externalCondition);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [actions, availableActions, externalCondition]);

    // Initial data fetch
    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            fetchData();

            setTimeout(() => {
                pageChangeInitialized.current = true;
                rowsPerPageInitialized.current = true;
                sortingInitialized.current = true;
            }, 500);
        }
    }, [fetchData, title]);


    //handlePrint
    const handlePrint = useCallback(async (row) => {
        try {
            await actions.print(row.id);
        } catch (error) {
            console.error('Printing error:', error);
        }
    }, [actions]);

    const handleView = useCallback(async (row) => {
        if (ViewComponent && actionType === 'sidebar') {
            toggleSidebar({
                className: `${sidebarClassName}`,
                title: `${title}`,
                content: (
                    <ViewComponent
                        initialData={row}
                        onClose={closeSidebar}
                    />
                ),
            });
            return;
        }
        window.open(`/${urlKey}/view/${row.id}?preview=true`, '_blank');
        return;
    }, [urlKey, ViewComponent, actionType, sidebarClassName, title, toggleSidebar, closeSidebar]);


    // Handle create with proper state update
    const handlePayment = useCallback((row) => {
        toggleSidebar({
            className: `${sidebarClassName}`,
            title: `Update Payment`,
            content: (
                <SubActionComponent
                    initialData={row}
                    onSave={async (data) => {
                        try {
                            const response = await actions.payment(row.id, data);
                            closeSidebar();
                            await fetchData(); // Reload data after successful creation
                            return response;
                        } catch (error) {
                            console.error('Error creating item:', error);
                            throw error;
                        }
                    }}
                    onClose={closeSidebar}
                />
            ),
        });
    }, [title, actions, fetchData, toggleSidebar, closeSidebar]);



    // Handle create with proper state update
    const handleCreate = useCallback(() => {
        if (actionType === 'url' && urlKey) {
            navigate(`/${urlKey}/create`);
            return;
        }

        toggleSidebar({
            className: `${sidebarClassName}`,
            title: `Create ${title}`,
            content: (
                <FormComponent
                    onSave={async (data) => {
                        try {
                            const response = await actions.create(data);
                            closeSidebar();
                            await fetchData(); // Reload data after successful creation
                            return response;
                        } catch (error) {
                            console.error('Error creating item:', error);
                            throw error;
                        }
                    }}
                    onClose={closeSidebar}
                />
            ),
        });
    }, [title, actions, fetchData, toggleSidebar, closeSidebar, actionType, urlKey, navigate]);

    // Handle edit with proper state update
    const handleEdit = useCallback((row) => {
        if (actionType === 'url' && urlKey) {
            window.open(`/${urlKey}/edit/${row.id}`, '_blank');
            return;
        }

        toggleSidebar({
            className: `${sidebarClassName}`,
            title: `Edit ${title}`,
            content: (
                <FormComponent
                    initialData={row}
                    onSave={async (data) => {
                        try {
                            const response = await actions.update(row.id, data);
                            closeSidebar();
                            await fetchData(); // Reload data after successful update
                            return response;
                        } catch (error) {
                            throw error;
                        }
                    }}
                    onClose={closeSidebar}
                />
            ),
        });
    }, [title, actions, fetchData, toggleSidebar, closeSidebar, actionType, urlKey]);

    // Handle delete with proper state update
    const handleDelete = useCallback(async (row) => {
        try {
            const response = await actions.delete(row.id);
            if (response) {
                await fetchData(); // Reload data after successful deletion
            }
            return response;
        } catch (error) {
            return false;
        }
    }, [actions, fetchData]);

    // Handle page change with proper state update
    const handlePageChange = useCallback(async (page) => {
        if (!isInitialized.current || !pageChangeInitialized.current) {
            return;
        }

        actions.setPagination({ ...state.pagination, current_page: page });
        await fetchData({ page: page });
    }, [actions, state.pagination, fetchData]);

    // Handle rows per page change with proper state update
    const handlePerRowsChange = useCallback(async (newPerPage, page) => {
        if (!isInitialized.current || !rowsPerPageInitialized.current) {
            return;
        }

        actions.setPagination({ ...state.pagination, per_page: newPerPage, current_page: page });
        await fetchData({ page: page, per_page: newPerPage });
    }, [actions, state.pagination, fetchData]);

    // Handle sort with proper state update
    const handleSort = useCallback(async (column, sortDirection) => {
        if (!isInitialized.current || !sortingInitialized.current) {
            return;
        }

        actions.setSort({ ...state.sort, field: column.sortField, direction: sortDirection });
        await fetchData({ sort_field: column.sortField, sort_direction: sortDirection });
    }, [actions, state.sort, fetchData]);

    // Search handling with proper state update
    const searchTimeout = useRef(null);
    const handleSearch = useCallback(async (event) => {
        const input = event.target.value;
        if (actions.search) {
            actions.search(input);

            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }

            searchTimeout.current = setTimeout(async () => {
                actions.setPagination({ ...state.pagination, current_page: 1 });
                await fetchData({ current_page: 1, search: input });
            }, 500);
        }
    }, [actions, state.pagination, fetchData]);

    // Filter bar update with proper state update
    const handleFilterBarUpdate = useCallback(async (data) => {
        if (data.action === 'fetchList') {
            await fetchData({}, data.filter);
        }
        if (data.action === 'fetchListClassicFilters') {
            await fetchData();
        }
    }, [fetchData]);


    // Cleanup
    useEffect(() => {
        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    }, []);

    // Memoize columns with position control
    const enhancedColumns = useMemo(() => {
        if (!availableActions.includes('actions')) {
            return columns;
        }

        const actionsColumn = {
            id: 'actions',
            name: 'Actions',
            width: actionsColumnWidth,
            ...(enableResize && actionsColumnResizable ? { resizable: true } : {}),
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    {availableActions.includes('print') && (
                        <button
                            onClick={() => handlePrint(row)}
                            className="btn btn-secondary btn-sm"
                            title="Print"
                        >
                            <i className="fas fa-print text-white"></i>
                        </button>
                    )}
                    {availableActions.includes('payment') && (
                        <button
                            onClick={() => handlePayment(row)}
                            className="btn btn-primary btn-sm"
                            title="Payment"
                        >
                            <i className="fas fa-money-bill text-white"></i>
                        </button>
                    )}
                    {availableActions.includes('view') && (
                        <button
                            onClick={() => handleView(row)}
                            className="btn btn-warning btn-sm"
                            title="View"
                        >
                            <i className="fas fa-eye text-white"></i>
                        </button>
                    )}
                    {availableActions.includes('edit') && (!rowActionConditions.edit || rowActionConditions.edit(row)) && (
                        <button
                            onClick={() => handleEdit(row)}
                            className="btn btn-primary btn-sm"
                            title="Edit"
                        >
                            <i className="fas fa-edit text-white"></i>
                        </button>
                    )}
                    {availableActions.includes('delete') && (!rowActionConditions.delete || rowActionConditions.delete(row)) && (
                        <button
                            onClick={() => handleDelete(row)}
                            className="btn btn-danger btn-sm"
                            title="Delete"
                        >
                            <i className="fas fa-trash text-white"></i>
                        </button>
                    )}
                    {customActions.map(action => {
                        const isVisible = action.condition ? action.condition(row) : true;
                        return isVisible ? (
                            <button
                                key={action.key}
                                onClick={() => handleCustomAction && handleCustomAction(action.key, row)}
                                className={`btn ${action.buttonClass || 'btn-info'} btn-sm`}
                                title={action.label}
                            >
                                <i className={`${action.icon || 'fas fa-undo'} text-white`}></i>
                            </button>
                        ) : null;
                    })}
                </div>
            ),
            ignoreRowClick: true
        };

        // Handle different position options
        if (actionsColumnPosition === 'start') {
            return [actionsColumn, ...columns];
        } else if (actionsColumnPosition === 'end') {
            return [...columns, actionsColumn];
        } else if (typeof actionsColumnPosition === 'number') {
            const newColumns = [...columns];
            newColumns.splice(actionsColumnPosition, 0, actionsColumn);
            return newColumns;
        } else {
            // Default to end if invalid position
            return [...columns, actionsColumn];
        }
    }, [columns, availableActions, actionsColumnWidth, actionsColumnPosition, enableResize, actionsColumnResizable, handlePrint, handlePayment, handleView, handleEdit, handleDelete, customActions, handleCustomAction, rowActionConditions]);

    return (
        <div className="row">
            <div className="col-lg-12">
                {customComponents.before && customComponents.before()}
                <div className="card">
                    <div className="card-body">
                        <FilterBar
                            title={title}
                            name={moduleName}
                            identifier={moduleKey}
                            module={state}
                            onInputSearch={handleSearch}
                            onDownloadClick={handleDownload}
                            onCreateButtonClick={handleCreate}
                            availableActions={availableActions}
                            onFilterBarUpdate={handleFilterBarUpdate}
                            setDefaultFilter={actions.setDefaultFilter}
                            changeCondition={actions.changeCondition}
                            addNewCondition={actions.addNewCondition}
                            updateOptionRule={actions.updateOptionRule}
                            updateOptionValue={actions.updateOptionValue}
                            removeCondition={actions.removeCondition}
                            saveFilterCondition={actions.saveFilterCondition}

                            onClassicFilterChange={actions.onClassicFilterChange}
                            hideClassicFilter={hideClassicFilter}
                        />
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            {title && <h5 className="font-size-17 me-3 mb-0">Manage {title}</h5>}
                            {customViewComponent}
                        </div>
                        {tableToolbar && (
                            <div className="mt-3">
                                {typeof tableToolbar === 'function' ? tableToolbar() : tableToolbar}
                            </div>
                        )}
                        {
                            !noTable ? (
                                <>
                                    <div className="row mt-2">
                                        <div className="col-md-12">
                                            <ReusableDataTable
                                                columns={enhancedColumns}
                                                data={state.list}
                                                loading={state.loading}
                                                pagination={state.pagination}
                                                paginationDefaultPage={1}
                                                noInitialSort={true}
                                                onChangePage={handlePageChange}
                                                onChangeRowsPerPage={handlePerRowsChange}
                                                onSort={handleSort}
                                                selectableRows={selectableRows}
                                                selectedIds={selectedIds || []}
                                                onSelectedRowsChange={onSelectedRowsChange}
                                                onClickedRow={onClickedRow}
                                                clickedRowHighlight={clickedRowHighlight}
                                                enableResize={enableResize}
                                            />
                                        </div>
                                    </div>
                                </>
                            )
                            : (
                                <>
                                
                                </>
                            )
                        }
                        {customComponents.after && customComponents.after()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GenericPage; 